// One row in the message list. Routes by `kind` to the matching
// renderer (text / snippet / file / secret) and decorates with the
// shared chrome: avatar + name popover, timestamp, reply
// quote-band, reaction strip, mention highlight.
//
// Privacy boundary (Reqs 10.10, 14.5): when `kind === "secret"`
// every visible surface — main body, search snippets, mention
// previews, notes view, etc. — must render only the placeholder
// text. The actual ciphertext is provided as a click-to-reveal
// mode on the row itself; no other surface ever shows it.

import {
  Copy,
  Eye,
  EyeOff,
  Files,
  Lock,
  MoreHorizontal,
  Pin,
  Pencil,
  Reply,
  ShieldAlert,
  Smile,
  Trash2,
  UserMinus,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Popover,
  PopoverTrigger,
  cn,
} from "@link/ui";

import { MemberProfilePopover, type MemberProfile } from "../MemberProfilePopover";
import { MessageContextMenu } from "./MessageContextMenu";
import { BadgeRow } from "../BadgeRow";
import { verifiedBadgeOnly } from "../../lib/badges";
import {
  renderForSurface,
  SECRET_PLACEHOLDER,
} from "../../lib/secrets-placeholder";
import type { MessageView } from "../../types/ui";
import { isIgnored, onIgnoredAccountsChanged } from "../../lib/ignored";
import { downloadAndSaveFile } from "../../lib/upload";
import { useSelf } from "../../state/SelfSession";
import { EmojiPicker, type PickerEmoji } from "./EmojiPicker";
import { api } from "../../lib/api";

interface Props {
  message: MessageView;
  /** Build the popover member shape for a click on the author. */
  asMember: (m: MessageView) => MemberProfile;
  /** Optional callbacks for mutating actions. */
  onReply?: (m: MessageView) => void;
  onReact?: (m: MessageView, emoji: string) => void;
  onTogglePin?: (m: MessageView) => void;
  onEdit?: (m: MessageView, body: string) => Promise<void>;
  onDelete?: (m: MessageView) => Promise<void>;
  /** DMs keep inline identity decoration limited to the verified staff mark. */
  badgeDisplay?: "all" | "verified";
  customEmojis?: PickerEmoji[];
  /** Resolve an @username into the compact profile shown when the mention is clicked. */
  resolveMention?: (username: string) => MemberProfile | undefined;
}

export function MessageItem({
  message,
  asMember,
  onReply,
  onReact,
  onTogglePin,
  onEdit,
  onDelete,
  badgeDisplay = "all",
  customEmojis = [],
  resolveMention,
}: Props) {
  const self = useSelf();
  // Right-click context menu state. Position is captured at the
  // exact cursor location so the floating menu reads as native.
  const [ctxOpen, setCtxOpen] = useState(false);
  const [ctxPos, setCtxPos] = useState<{ x: number; y: number } | null>(null);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(message.body);
  const [editBusy, setEditBusy] = useState(false);
  const [shiftPressed, setShiftPressed] = useState(false);

  useEffect(() => {
    const update = (event: KeyboardEvent): void => setShiftPressed(event.shiftKey);
    const clear = (): void => setShiftPressed(false);
    window.addEventListener("keydown", update);
    window.addEventListener("keyup", update);
    window.addEventListener("blur", clear);
    return () => {
      window.removeEventListener("keydown", update);
      window.removeEventListener("keyup", update);
      window.removeEventListener("blur", clear);
    };
  }, []);
  useEffect(() => {
    if (!editing) setEditBody(message.body);
  }, [editing, message.body]);

  const onContextMenu = (e: React.MouseEvent<HTMLLIElement>): void => {
    e.preventDefault();
    setCtxPos({ x: e.clientX, y: e.clientY });
    setCtxOpen(true);
  };

  // Optimistic local rows use authorXid === "me" until the API response
  // replaces them with the account-backed message.
  const authoredByMe =
    message.authorXid === "me" || message.authorXid === self.identity.accountXid;
  const member = asMember(message);
  const [ignored, setIgnored] = useState(() => isIgnored(message.authorXid));
  const [showIgnored, setShowIgnored] = useState(false);
  useEffect(
    () => onIgnoredAccountsChanged(() => setIgnored(isIgnored(message.authorXid))),
    [message.authorXid],
  );

  if (ignored && !authoredByMe && !showIgnored) {
    return (
      <li className="flex items-center gap-2 rounded-lg border border-cloud-200 bg-cloud-50/70 px-3 py-2 text-xs text-cloud-500">
        <UserMinus className="h-4 w-4" />
        <span className="flex-1">Message from ignored user</span>
        <button type="button" onClick={() => setShowIgnored(true)} className="font-semibold text-cloud-700 hover:underline">
          Show
        </button>
      </li>
    );
  }

  return (
    <li
      onContextMenu={onContextMenu}
      className={cn(
        "group relative flex gap-3 rounded-lg px-2 py-1 transition-colors",
        message.mentionsMe === true
          ? "bg-strawberry-50/70 ring-1 ring-strawberry-200"
          : "hover:bg-cloud-50",
      )}
    >
      {/* Mention indicator pip on the gutter. */}
      {message.mentionsMe === true && (
        <span className="absolute left-0 top-2 h-[calc(100%-1rem)] w-0.5 rounded-r-full bg-strawberry-500" />
      )}

      <button
        type="button"
        aria-label="More message actions"
        onClick={(event) => {
          event.stopPropagation();
          const rect = event.currentTarget.getBoundingClientRect();
          setCtxPos({ x: rect.right, y: rect.bottom + 4 });
          setCtxOpen(true);
        }}
        className="absolute right-1 top-1 z-10 flex h-8 w-8 items-center justify-center rounded-lg border border-cloud-200 bg-white/95 text-cloud-500 shadow-sm backdrop-blur active:bg-cloud-100 md:hidden"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex-shrink-0 rounded-full transition-transform hover:scale-105"
            aria-label={`Open profile for ${message.authorName}`}
          >
            <Avatar className="h-9 w-9">
              {message.authorAvatarUrl != null ? (
                <AvatarImage src={message.authorAvatarUrl} alt="" />
              ) : null}
              <AvatarFallback>
                {message.authorName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </button>
        </PopoverTrigger>
        <MemberProfilePopover
          side="right"
          align="start"
          member={member}
        />
      </Popover>

      <div className="min-w-0 flex-1">
        {/* Reply quote band */}
        {message.replyTo !== undefined && (
          <div className="mb-1 flex items-center gap-2 rounded-md bg-cloud-50 px-2 py-1 text-[11px] text-cloud-500">
            <Reply className="h-3 w-3 text-cloud-400" />
            <span className="font-semibold text-cloud-700">
              {message.replyTo.authorName}
            </span>
            <span className="truncate">{message.replyTo.preview}</span>
          </div>
        )}

        <div className="flex items-baseline gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="font-semibold text-cloud-800 hover:underline"
              >
                {message.authorName}
              </button>
            </PopoverTrigger>
            <MemberProfilePopover
              side="right"
              align="start"
              member={member}
            />
          </Popover>
          <BadgeRow
            badges={badgeDisplay === "verified"
              ? verifiedBadgeOnly(member.badges ?? [])
              : member.badges ?? []}
            max={badgeDisplay === "verified" ? 1 : 6}
          />
          <span className="text-[11px] text-cloud-400">
            {message.timestamp}
          </span>
          {message.editedAt != null ? <span className="text-[10px] text-cloud-400">(edited)</span> : null}
          {message.pinned === true && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-strawberry-600">
              <Pin className="h-3 w-3" />
              Pinned
            </span>
          )}
        </div>

        {/* Body — routed by kind. */}
        {editing ? (
          <div className="mt-1 space-y-1.5">
            <textarea
              autoFocus
              value={editBody}
              onChange={(event) => setEditBody(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setEditing(false);
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (!editBusy && editBody.trim().length > 0 && onEdit !== undefined) {
                    setEditBusy(true);
                    void onEdit(message, editBody).then(() => setEditing(false)).finally(() => setEditBusy(false));
                  }
                }
              }}
              className="min-h-20 w-full resize-y rounded-xl border border-violet-300 bg-white px-3 py-2 text-sm text-cloud-800 outline-none ring-2 ring-violet-200/60"
            />
            <p className="text-[10px] text-cloud-400">Escape to cancel · Enter to save · Shift+Enter for a new line</p>
          </div>
        ) : (
          <Body message={message} accountXid={self.identity.accountXid} resolveMention={resolveMention} />
        )}

        {/* Reactions */}
        {message.reactions !== undefined &&
          message.reactions.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {message.reactions.map((r) => (
                <button
                  key={r.emoji}
                  type="button"
                  onClick={() => onReact?.(message, r.emoji)}
                  disabled={onReact === undefined}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 transition-colors",
                    r.mine
                      ? "bg-strawberry-50 text-strawberry-700 ring-strawberry-200"
                      : "bg-white text-cloud-600 ring-cloud-200 hover:bg-cloud-50",
                  )}
                >
                  <span>{customEmojis.find((emoji) => emoji.value === r.emoji)?.imageUrl !== undefined ? <img src={customEmojis.find((emoji) => emoji.value === r.emoji)!.imageUrl} alt={r.emoji} className="h-4 w-4 object-contain" /> : r.emoji}</span>
                  <span className="font-semibold">{r.count}</span>
                </button>
              ))}
            </div>
          )}
      </div>

      {/* Hover toolbar */}
      <div className="absolute right-2 top-1 hidden items-center gap-0.5 rounded-md border border-cloud-200 bg-white px-0.5 py-0.5 shadow-sm md:group-hover:flex">
        {shiftPressed ? <ToolbarBtn title="Copy message" onClick={() => void navigator.clipboard.writeText(message.body)}><Copy className="h-3.5 w-3.5" /></ToolbarBtn> : null}
        {shiftPressed && authoredByMe && onEdit !== undefined ? <ToolbarBtn title="Edit message" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5" /></ToolbarBtn> : null}
        {shiftPressed && authoredByMe && onDelete !== undefined ? <ToolbarBtn title="Delete message" onClick={() => void onDelete(message)}><Trash2 className="h-3.5 w-3.5" /></ToolbarBtn> : null}
        {onReact !== undefined ? <EmojiPicker custom={customEmojis} onSelect={(emoji) => onReact(message, emoji)}><button type="button" title="Add reaction" aria-label="Add reaction" className="flex h-6 w-6 items-center justify-center rounded text-cloud-500 hover:bg-cloud-100 hover:text-strawberry-600"><Smile className="h-3.5 w-3.5" /></button></EmojiPicker> : null}
        {onReply !== undefined ? <ToolbarBtn title="Reply" onClick={() => onReply(message)}>
          <Reply className="h-3.5 w-3.5" />
        </ToolbarBtn> : null}
        {onTogglePin !== undefined ? <ToolbarBtn title="Pin" onClick={() => onTogglePin(message)}>
          <Pin className="h-3.5 w-3.5" />
        </ToolbarBtn> : null}
      </div>

      <MessageContextMenu
        message={message}
        position={ctxPos}
        open={ctxOpen}
        onOpenChange={setCtxOpen}
        authoredByMe={authoredByMe}
        onAddReaction={onReact === undefined ? undefined : () => onReact(message, "👍")}
        onReply={onReply === undefined ? undefined : () => onReply(message)}
        onPin={onTogglePin === undefined ? undefined : () => onTogglePin(message)}
        onEdit={onEdit === undefined ? undefined : () => setEditing(true)}
        onDelete={onDelete === undefined ? undefined : () => void onDelete(message)}
      />
    </li>
  );
}

function ToolbarBtn({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className="flex h-6 w-6 items-center justify-center rounded text-cloud-500 hover:bg-cloud-100 hover:text-strawberry-600"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Body routing
// ---------------------------------------------------------------------------

function Body({
  message,
  accountXid,
  resolveMention,
}: {
  message: MessageView;
  accountXid: string;
  resolveMention?: (username: string) => MemberProfile | undefined;
}) {
  const kind = message.kind ?? "text";
  if (kind === "secret") return <SecretBody message={message} />;
  if (kind === "snippet") return <SnippetBody message={message} />;
  if (kind === "file") {
    return <FileBody message={message} accountXid={accountXid} />;
  }
  return <TextBody body={message.body} mentionsMe={message.mentionsMe ?? false} resolveMention={resolveMention} />;
}

// ---------------------------------------------------------------------------
// Text — Markdown subset (bold / italic / strike / inline code / links / lists)
// ---------------------------------------------------------------------------

function TextBody({ body, mentionsMe, resolveMention }: { body: string; mentionsMe: boolean; resolveMention?: (username: string) => MemberProfile | undefined }) {
  // Split on fenced code blocks first so renderInline doesn't try to
  // tokenize what's inside them.
  const parts = splitFences(body);
  return (
    <div
      className={cn(
        "mt-0.5 break-words text-sm",
        mentionsMe ? "text-cloud-800" : "text-cloud-700",
      )}
    >
      {parts.map((part, i) =>
        part.kind === "fence" ? (
          <CodeBlock key={i} body={part.body} language={part.lang} />
        ) : (
          <Paragraphs key={i} body={part.body} resolveMention={resolveMention} />
        ),
      )}
      <InviteCards body={body} />
      <GifEmbeds body={body} />
    </div>
  );
}

function GifEmbeds({ body }: { body: string }) {
  const urls = Array.from(body.matchAll(/https:\/\/[^\s]+\.(?:gif|webp)(?:\?[^\s]*)?/gi), (match) => match[0]);
  return <>{Array.from(new Set(urls)).map((url) => <img key={url} src={url} alt="Shared GIF" loading="lazy" className="mt-2 max-h-80 max-w-full rounded-2xl border border-cloud-200 bg-cloud-50 object-contain" />)}</>;
}

function InviteCards({ body }: { body: string }) {
  const tokens = Array.from(body.matchAll(/(?:https?:\/\/[^\s/]+)?\/invite\/([A-Za-z0-9_-]+)/g), (match) => match[1]).filter((value): value is string => value !== undefined);
  return <>{Array.from(new Set(tokens)).map((token) => <InviteCard key={token} token={token} />)}</>;
}

function InviteCard({ token }: { token: string }) {
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.invitePreviews.get>> | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { let cancelled = false; void api.invitePreviews.get(token).then((result) => { if (!cancelled) setPreview(result); }).catch(() => { if (!cancelled) setError(true); }); return () => { cancelled = true; }; }, [token]);
  if (error) return null;
  if (preview === null) return <div className="mt-3 h-52 w-full max-w-md animate-pulse rounded-3xl bg-cloud-100" />;
  const open = async (): Promise<void> => { setBusy(true); try { if (!preview.is_member) await api.spaces.acceptInvite(token); window.dispatchEvent(new CustomEvent("link:open-space", { detail: { spaceXid: preview.space.xid } })); } finally { setBusy(false); } };
  return <div className="mt-3 w-full max-w-md overflow-hidden rounded-3xl border border-cloud-300 bg-white shadow-lg shadow-black/10"><div className="h-24 bg-gradient-to-br from-violet-600 via-fuchsia-500 to-rose-300" /><div className="p-5"><div className="-mt-14 flex h-20 w-20 items-center justify-center overflow-hidden rounded-[22px] bg-cloud-900 text-xl font-black text-white ring-4 ring-white shadow-md">{preview.space.icon_url !== null ? <img src={preview.space.icon_url} alt="" className="h-full w-full object-cover" /> : preview.space.name.slice(0, 2)}</div><p className="mt-4 truncate text-lg font-black text-cloud-900">{preview.space.name}</p><div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-cloud-500"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />{preview.space.online_count} online</span><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cloud-300" />{preview.space.member_count} member{preview.space.member_count === 1 ? "" : "s"}</span><span>Est. {new Date(preview.space.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</span></div><button type="button" disabled={busy} onClick={() => void open()} className="mt-5 h-11 w-full rounded-xl bg-emerald-600 text-sm font-bold text-white shadow-sm shadow-emerald-900/20 transition hover:-translate-y-0.5 hover:bg-emerald-500 hover:shadow-md disabled:translate-y-0 disabled:opacity-50">{preview.is_member ? "Go to Server" : "Join Server"}</button></div></div>;
}

interface FencePart {
  kind: "fence";
  lang: string;
  body: string;
}
interface ProsePart {
  kind: "prose";
  body: string;
}

function splitFences(body: string): Array<FencePart | ProsePart> {
  const out: Array<FencePart | ProsePart> = [];
  const re = /```([a-zA-Z0-9_+-]*)\n([\s\S]*?)```/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > cursor) {
      out.push({ kind: "prose", body: body.slice(cursor, m.index) });
    }
    out.push({ kind: "fence", lang: m[1] ?? "", body: m[2] ?? "" });
    cursor = m.index + m[0].length;
  }
  if (cursor < body.length) {
    out.push({ kind: "prose", body: body.slice(cursor) });
  }
  return out;
}

function Paragraphs({ body, resolveMention }: { body: string; resolveMention?: (username: string) => MemberProfile | undefined }) {
  // Lists land on their own paragraph; everything else flows inline
  // with `\n` as a soft break.
  const lines = body.split("\n");
  const out: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  let key = 0;

  const flushList = (): void => {
    if (listBuffer.length === 0) return;
    out.push(
      <ul key={`l-${key++}`} className="my-1 ml-5 list-disc text-cloud-700 marker:text-strawberry-400">
        {listBuffer.map((item, i) => (
          <li key={i}>{renderInline(item, resolveMention)}</li>
        ))}
      </ul>,
    );
    listBuffer = [];
  };

  let para: string[] = [];
  const flushPara = (): void => {
    if (para.length === 0) return;
    out.push(
      <p key={`p-${key++}`} className="leading-snug">
        {renderInline(para.join("\n"), resolveMention)}
      </p>,
    );
    para = [];
  };

  for (const line of lines) {
    const listMatch = /^\s*[-*]\s+(.*)$/.exec(line);
    if (listMatch !== null) {
      flushPara();
      listBuffer.push(listMatch[1] ?? "");
      continue;
    }
    flushList();
    if (line.trim().length === 0) {
      flushPara();
    } else {
      para.push(line);
    }
  }
  flushList();
  flushPara();

  return <>{out}</>;
}

function renderInline(text: string, resolveMention?: (username: string) => MemberProfile | undefined): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  // bold ** | italic _ | strike ~~ | code ` | link [t](u) | mention @x
  const re =
    /(\*\*([^*]+)\*\*|_([^_]+)_|~~([^~]+)~~|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|@([a-zA-Z0-9_]+))/g;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > cursor) {
      out.push(<span key={key++}>{text.slice(cursor, m.index)}</span>);
    }
    if (m[2] !== undefined) {
      out.push(
        <strong key={key++} className="font-semibold text-cloud-800">
          {m[2]}
        </strong>,
      );
    } else if (m[3] !== undefined) {
      out.push(
        <em key={key++} className="italic">
          {m[3]}
        </em>,
      );
    } else if (m[4] !== undefined) {
      out.push(
        <s key={key++} className="opacity-70">
          {m[4]}
        </s>,
      );
    } else if (m[5] !== undefined) {
      out.push(
        <code
          key={key++}
          className="rounded bg-cloud-100 px-1 py-0.5 font-mono text-[12.5px] text-cloud-800"
        >
          {m[5]}
        </code>,
      );
    } else if (m[6] !== undefined && m[7] !== undefined) {
      out.push(
        <a
          key={key++}
          href={m[7]}
          target="_blank"
          rel="noreferrer"
          className="text-strawberry-700 underline decoration-strawberry-300 underline-offset-2 hover:decoration-strawberry-500"
        >
          {m[6]}
        </a>,
      );
    } else if (m[8] !== undefined) {
      const username = m[8];
      const specialMention = username.toLowerCase() === "everyone" || username.toLowerCase() === "here";
      const mentioned = resolveMention?.(username);
      out.push(mentioned === undefined ? (
        <span key={key++} className={cn("rounded-md px-1 py-0.5 font-semibold", specialMention ? "bg-violet-100 text-violet-700" : "bg-violet-50 text-violet-700")}>@{username}</span>
      ) : (
        <Popover key={key++}>
          <PopoverTrigger asChild>
            <button type="button" className="rounded-md bg-violet-50 px-1 py-0.5 font-semibold text-violet-700 hover:bg-violet-100 hover:underline">@{username}</button>
          </PopoverTrigger>
          <MemberProfilePopover side="top" align="start" member={mentioned} />
        </Popover>
      ));
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) {
    out.push(<span key={key++}>{text.slice(cursor)}</span>);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Snippet — code-block default with copy button + line counter
// ---------------------------------------------------------------------------

function SnippetBody({ message }: { message: MessageView }) {
  return (
    <CodeBlock
      body={message.body}
      language={message.language ?? "text"}
      collapsible
    />
  );
}

function CodeBlock({
  body,
  language,
  collapsible = false,
}: {
  body: string;
  language: string;
  collapsible?: boolean;
}) {
  const lines = body.split("\n");
  const lineCount = lines.length;
  const tooLong = collapsible && lineCount > 200;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const visible = tooLong && !expanded ? lines.slice(0, 200) : lines;

  const onCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(body);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="link-code-block my-1.5 overflow-hidden rounded-xl border">
      <div className="link-code-header flex items-center gap-2 border-b px-3 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          {language || "text"}
        </span>
        <span className="link-code-muted text-[10px]">
          · {lineCount} line{lineCount === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          onClick={() => void onCopy()}
          className="link-code-copy ml-auto inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold"
        >
          <Copy className="h-3 w-3" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="link-code-body max-h-[480px] overflow-auto px-3 py-2 text-[12px] leading-5">
        <code>
          <HighlightedCode language={language} lines={visible} />
        </code>
      </pre>
      {tooLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="link-code-more block w-full border-t px-3 py-1 text-[11px] font-semibold"
        >
          {expanded
            ? "Collapse"
            : `Show ${lineCount - 200} more line${lineCount - 200 === 1 ? "" : "s"}`}
        </button>
      )}
    </div>
  );
}

function HighlightedCode({ language, lines }: { language: string; lines: string[] }) {
  const normalized = language.trim().toLowerCase();
  if (normalized !== "yaml" && normalized !== "yml") {
    return <>{lines.join("\n")}</>;
  }
  return (
    <>
      {lines.map((line, index) => {
        const commentAt = line.indexOf("#");
        const source = commentAt >= 0 ? line.slice(0, commentAt) : line;
        const comment = commentAt >= 0 ? line.slice(commentAt) : "";
        const match = /^(\s*(?:-\s+)?)([^:#]+)(:)(.*)$/.exec(source);
        return (
          <span key={`${index}-${line}`} className="block min-h-5">
            {match === null ? source : (
              <>
                <span>{match[1]}</span>
                <span className="text-sky-300">{match[2]}</span>
                <span className="text-cloud-400">{match[3]}</span>
                <span className="text-amber-200">{match[4]}</span>
              </>
            )}
            {comment.length > 0 ? <span className="text-emerald-300/80">{comment}</span> : null}
          </span>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// File — image / generic preview tile
// ---------------------------------------------------------------------------

function FileBody({
  message,
  accountXid,
}: {
  message: MessageView;
  accountXid: string;
}) {
  const file = message.file;
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  if (file === undefined) return null;

  const download = async (): Promise<void> => {
    if (file.attachmentXid === undefined || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadAndSaveFile(file.attachmentXid, accountXid);
    } catch (cause: unknown) {
      setDownloadError(
        cause instanceof Error ? cause.message : "Download failed.",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="my-1 flex flex-wrap items-center gap-3 rounded-lg border border-cloud-200 bg-cloud-50 px-3 py-2">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-grape-100 text-grape-700">
        <Files className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-cloud-800">
          {file.name}
        </p>
        <p className="text-[11px] text-cloud-500">
          {file.sizeLabel} · {file.mime}
        </p>
      </div>
      {file.attachmentXid !== undefined ? (
        <button
          type="button"
          disabled={downloading}
          onClick={() => void download()}
          className="rounded-md bg-white px-2 py-1 text-xs font-semibold text-strawberry-700 ring-1 ring-strawberry-200 hover:bg-strawberry-50"
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
      ) : (
        <span
          className="rounded-md bg-white px-2 py-1 text-xs font-semibold text-cloud-400 ring-1 ring-cloud-200"
          title="Uploading…"
        >
          Uploading…
        </span>
      )}
      {downloadError !== null ? (
        <p className="basis-full text-[11px] font-medium text-red-600" role="alert">
          {downloadError}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Secret — click-to-reveal blur, hold-to-reveal on touch (≥250ms),
// screenshot warning toast on first reveal of a session, copy-button
// clears clipboard after 30s. (Reqs 10.4, 10.8, 10.9)
// ---------------------------------------------------------------------------

let SECRETS_WARNED_THIS_SESSION = false;

function SecretBody({ message }: { message: MessageView }) {
  const [revealed, setRevealed] = useState(false);
  const [holding, setHolding] = useState(false);
  const [warned, setWarned] = useState(false);
  const [copied, setCopied] = useState(false);
  const holdTimerRef = useRef<number | null>(null);

  // Auto-collapse after a short timer when the user releases hold.
  // Click-to-reveal is sticky until they explicitly hide it.
  useEffect(() => {
    if (!holding && holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, [holding]);

  const onReveal = (): void => {
    if (!SECRETS_WARNED_THIS_SESSION) {
      SECRETS_WARNED_THIS_SESSION = true;
      setWarned(true);
      window.setTimeout(() => setWarned(false), 4000);
    }
    setRevealed(true);
  };

  const onTouchStart = (): void => {
    holdTimerRef.current = window.setTimeout(() => {
      setHolding(true);
      onReveal();
    }, 250);
  };
  const onTouchEnd = (): void => {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHolding(false);
    setRevealed(false);
  };

  const onCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(message.body);
    setCopied(true);
    window.setTimeout(async () => {
      // Clear the clipboard after 30s to mitigate paste-buffer
      // leakage (Req 10.9).
      try {
        await navigator.clipboard.writeText("");
      } catch {
        // some browsers reject programmatic writes in the
        // background — best-effort.
      }
      setCopied(false);
    }, 30_000);
  };

  const showActual = revealed || holding;
  const decision = renderForSurface({
    kind: "secret",
    surface: "channel",
    body: message.body,
    revealed: showActual,
  });
  // The placeholder string for the redacted state is canonical;
  // augment with our local affordance copy when redacted.
  const placeholder =
    decision.redacted ? `${SECRET_PLACEHOLDER} — tap or click to reveal` : decision.display;

  return (
    <div className="my-1 space-y-1">
      <div
        role="button"
        tabIndex={0}
        onClick={onReveal}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onReveal();
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        className={cn(
          "relative cursor-pointer select-none rounded-lg border border-strawberry-200 bg-strawberry-50 px-3 py-2 text-sm transition-all",
          showActual
            ? "text-cloud-800"
            : "text-strawberry-700 hover:bg-strawberry-100",
        )}
      >
        <div className="flex items-start gap-2">
          <Lock
            className={cn(
              "mt-0.5 h-3.5 w-3.5 flex-shrink-0",
              showActual ? "text-strawberry-500" : "text-strawberry-600",
            )}
          />
          <div className="min-w-0 flex-1">
            {showActual ? (
              <pre className="whitespace-pre-wrap break-words font-sans">
                {decision.display}
              </pre>
            ) : (
              <p className="italic">{placeholder}</p>
            )}
          </div>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-strawberry-700">
            {showActual ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            {showActual ? "Hide" : "Reveal"}
          </span>
        </div>
      </div>

      {showActual && (
        <div className="flex items-center gap-2 text-[11px] text-cloud-500">
          {message.expiresAt !== undefined && (
            <span>
              Expires {formatExpiration(message.expiresAt)}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void onCopy();
            }}
            className="ml-auto inline-flex items-center gap-1 rounded bg-white px-2 py-0.5 text-[11px] font-semibold text-strawberry-700 ring-1 ring-strawberry-200 hover:bg-strawberry-50"
          >
            <Copy className="h-3 w-3" />
            {copied ? "Copied — clears in 30s" : "Copy"}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setRevealed(false);
            }}
            className="rounded bg-white px-2 py-0.5 text-[11px] font-semibold text-cloud-600 ring-1 ring-cloud-200 hover:bg-cloud-50"
          >
            Hide
          </button>
        </div>
      )}

      {warned && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800 ring-1 ring-amber-200">
          <ShieldAlert className="h-3 w-3" />
          Heads up: anyone watching your screen can read this.
          Screenshots and screen-shares aren't blocked.
        </div>
      )}
    </div>
  );
}

function formatExpiration(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = t - Date.now();
  if (diff <= 0) return "now";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `in ${days}d`;
  const months = Math.floor(days / 30);
  return `in ${months}mo`;
}
